/* eslint-disable max-classes-per-file */
/**
 * @namespace mixin
 * @see module:@kids/lib/mixin
 */

/**
 * @module @kids/lib/mixin
 * @desc Über dieses Modul können die {@link mixin.mix|mix-Funktion} und alle Mixins importiert werden. Inspirationen {@link https://github.com/justinfagnani/mixwith.js|hier} und {@link http://justinfagnani.com/2015/12/21/real-mixins-with-javascript-classes/|hier}
 * @example
 * import {mix, Eventable, ParameterBag} from '@kids/lib/mixin';
 *
 * class Car extends mix().with(Eventable)
 * {
 *     drive()
 *     {
 *         this.fire('drive');
 *     }
 * }
 *
 * const methods = {
 *     sayHi()
 *     {
 *         console.log('hi');
 *     },
 *     set(...args)
 *     {
 *         // super ist nicht möglich, weil ein neues Objekt entsteht - deswegen garantierte
 *         // Utility-Methode nutzen!
 *         return this._callSuper('set', ...args);
 *     }
 * };
 * const hiSayerWithParameters = mix(methods).with(ParameterBag);
 */

/**
 * @static
 * @name Eventable
 * @type {Eventable}
 */
export {default as Eventable} from './mixin/Eventable';
/**
 * @static
 * @name ParameterBag
 * @type {ParameterBag}
 */
export {default as ParameterBag} from './mixin/ParameterBag';
/**
 * @static
 * @name Loadable
 * @type {Loadable}
 */
export {default as Loadable} from './mixin/Loadable';
/**
 * @static
 * @name Timeable
 * @type {Timeable}
 */
export {default as Timeable} from './mixin/Timeable';
/**
 * @static
 * @name Bindable
 * @type {Bindable}
 */
export {default as Bindable} from './mixin/Bindable';

const cachedApplications = [];
const EmptyClass = class {};

const findCachedApplication = (SuperClass, Mixin) =>
{
    for (const application of cachedApplications)
    {
        if (SuperClass === application.SuperClass && Mixin === application.Mixin)
        {
            return application.Mixed;
        }
    }
    return null;
};

const applyMixin = (SuperClass, Mixin) =>
{
    let Mixed = findCachedApplication(SuperClass, Mixin);
    if (!Mixed)
    {
        Mixed = Mixin(SuperClass);
        cachedApplications.push({SuperClass, Mixin, Mixed});
    }
    return Mixed;
};

const sortMixins = (Mixin1, Mixin2) => Mixin1.sorting - Mixin2.sorting;

class MixinClassBuilder
{
    /**
     * @global
     * @param {Function=} superClass Die Klasse, die von den Mixins erben soll. Falls keine Klasse übergeben wird,
     * wird eine leere Klasse als Template verwendet.
     */
    constructor(SuperClass)
    {
        this.SuperClass = SuperClass || EmptyClass;
    }

    /**
     * Fügt Mixins hinzu.
     * @param {...Mixin} mixins Mixins, die an die Klasse vererbt werden.
     * @return {Function} Die Klasse mit den hinzugefügten Mixins.
     * @memberof MixinClassBuilder
     */
    with(...mixins)
    {
        mixins.sort(sortMixins);
        return mixins.reduce((SuperClass, Mixin) => applyMixin(SuperClass, Mixin), this.SuperClass);
    }
}

let warningDisplayed = false;

// siehe: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/super?retiredLocale=de#methods_that_read_super.prop_do_not_behave_differently_when_bound_to_other_objects
const callSuperMethod = function(method, ...args)
{
    return Object.getPrototypeOf(this)[method].call(this, ...args);
};

class MixinObjectBuilder
{
    /**
     * @global
     * @param {Object} object Das Objekt, in welches etwas gemixt werden soll.
     */
    constructor(object, internal)
    {
        this.internal = internal;
        this.object = object;
        this.classBuilder = new MixinClassBuilder();
    }

    /**
     * Fügt Mixins hinzu.
     * @param {...Mixin} mixins Mixins, welche zum Objekt hinzugefügt werden sollen.
     * @return {Object} Das Objekt mit den hinzugefügten Mixins.
     * @memberof MixinObjectBuilder
     */
    with(...mixins)
    {
        const SuperClass = this.classBuilder.with(...mixins);
        const newObject = new SuperClass();
        const oldObject = this.object;

        let includesFunction = false;

        Object.getOwnPropertyNames(oldObject).forEach((key) =>
        {
            const desc = Object.getOwnPropertyDescriptor(oldObject, key);
            if (typeof desc.value === 'function')
            {
                includesFunction = true;
            }
            Object.defineProperty(newObject, key, desc);
        });

        Object.defineProperty(newObject, '_callSuper', {value: callSuperMethod});

        if (includesFunction && !this.internal &&
            process.env.NODE_ENV === 'development' && !warningDisplayed)
        {
            console.warn(
                'Take note: MixinObjectBuilder.with() will return a new object from now on.'
            );
            warningDisplayed = true;
        }

        return newObject;
    }
}

/**
 * @alias mixin.mix
 * @param {(Object|Function)=} mixedIn Das Objekt oder die Klasse, in welche etwas gemixt werden soll.
 * @return {MixinClassBuilder|MixinObjectBuilder} Mixin Builder, welcher die <b>with</b>-Methode zur Verfügung stellt,
 * die eine unbestimmte Anzahl von Mixins als Parameter empfangen kann.
 */
const mix = function(mixedIn, internal = false)
{
    if (typeof mixedIn === 'object')
    {
        if (Object.getPrototypeOf(mixedIn).constructor === Object)
        {
            return new MixinObjectBuilder(mixedIn, internal);
        }
        throw new TypeError('Can not mix into instances of any other prototype than Object');
    }
    else
    {
        return new MixinClassBuilder(mixedIn);
    }
};

export {
    /**
     * @static
     * @name mix
     * @type {mixin.mix}
     */
    mix,
    /**
     * @static
     * @name MixinClassBuilder
     * @type {MixinClassBuilder}
     */
    MixinClassBuilder,
    /**
     * @static
     * @name MixinObjectBuilder
     * @type {MixinObjectBuilder}
     */
    MixinObjectBuilder
};
